import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { favoritesApi } from '@/shared/api/categories';
import { toast } from '@/shared/ui';

type Kind = 'category' | 'subcategory';

interface FavoritesStoreState {
  categoryIds: Set<string>;
  subcategoryIds: Set<string>;
  loaded: boolean;
  toggle: (kind: Kind, id: string) => Promise<void>;
}

let loadPromise: Promise<void> | null = null;

function loadFavorites(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = favoritesApi
    .list()
    .then((res) => {
      const payload = res.data ?? { categoryIds: [], subcategoryIds: [] };
      useFavoritesStore.setState({
        categoryIds: new Set(payload.categoryIds ?? []),
        subcategoryIds: new Set(payload.subcategoryIds ?? []),
      });
    })
    .catch(() => {
      loadPromise = null;
    })
    .finally(() => {
      useFavoritesStore.setState({ loaded: true });
    });
  return loadPromise;
}

const useFavoritesStore = create<FavoritesStoreState>((set, get) => ({
  categoryIds: new Set<string>(),
  subcategoryIds: new Set<string>(),
  loaded: false,

  toggle: async (kind, id) => {
    const key = kind === 'category' ? 'categoryIds' : 'subcategoryIds';
    const wasFavorite = get()[key].has(id);
    const target = kind === 'category' ? { categoryId: id } : { subcategoryId: id };

    set((prev) => {
      const next = new Set(prev[key]);
      if (wasFavorite) next.delete(id);
      else next.add(id);
      return { [key]: next };
    });

    try {
      if (wasFavorite) await favoritesApi.remove(target);
      else await favoritesApi.add(target);
    } catch {
      set((prev) => {
        const next = new Set(prev[key]);
        if (wasFavorite) next.add(id);
        else next.delete(id);
        return { [key]: next };
      });
      toast.error('Не удалось обновить избранное');
    }
  },
}));

export function useFavorites() {
  const categoryIds = useFavoritesStore((s) => s.categoryIds);
  const subcategoryIds = useFavoritesStore((s) => s.subcategoryIds);
  const loaded = useFavoritesStore((s) => s.loaded);
  const toggle = useFavoritesStore((s) => s.toggle);

  useEffect(() => {
    void loadFavorites();
  }, []);

  const isFavorite = useCallback(
    (kind: Kind, id: string) =>
      kind === 'category' ? categoryIds.has(id) : subcategoryIds.has(id),
    [categoryIds, subcategoryIds],
  );

  return { isFavorite, toggle, loaded, state: { categoryIds, subcategoryIds } };
}
